import React from 'react';
import { User, MessageSquare, Clock, Users } from 'lucide-react';

const ContactList = ({ 
  conversations, 
  selectedConversation, 
  onConversationSelect, 
  loading, 
  error 
}) => {
  if (loading) {
    return (
      <div className="p-4 space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="flex items-center space-x-3 p-3 animate-pulse">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <div className="text-red-600 text-sm">{error}</div>
      </div>
    );
  }

  if (!conversations || conversations?.length === 0) {
    return (
      <div className="p-4 text-center">
        <MessageSquare className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">No conversations yet.</p>
        <p className="text-xs text-gray-400 mt-1">Start a new chat to get started.</p>
      </div>
    );
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date?.toLocaleDateString();
  };

  const getConversationTitle = (conversation) => {
    if (conversation?.title) return conversation?.title;
    
    if (conversation?.conversationType === 'direct' && conversation?.participants) {
      const otherParticipant = conversation?.participants?.find(p => p?.userId !== conversation?.createdBy);
      return otherParticipant?.user?.fullName || 'Unknown User';
    }
    
    return 'Group Chat';
  };

  const getConversationSubtitle = (conversation) => {
    if (conversation?.conversationType === 'group') {
      const participantCount = conversation?.participants?.length || 0;
      return `${participantCount} members`;
    }
    
    const otherParticipant = conversation?.participants?.find(p => p?.userId !== conversation?.createdBy);
    return otherParticipant?.user?.role || 'User';
  };

  return (
    <div className="overflow-y-auto h-full">
      {conversations?.map((conversation) => (
        <div
          key={conversation?.id}
          onClick={() => onConversationSelect(conversation)}
          className={`flex items-center space-x-3 p-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors ${
            selectedConversation?.id === conversation?.id 
              ? 'bg-blue-50 border-blue-200' : ''
          }`}
        >
          {/* Avatar */}
          <div className="flex-shrink-0 relative">
            {conversation?.conversationType === 'group' ? (
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
            )}
            {/* Unread indicator */}
            {conversation?.hasUnreadMessages && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
            )}
          </div>

          {/* Conversation Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {getConversationTitle(conversation)}
              </h3>
              {conversation?.latestMessage && (
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatTime(conversation?.latestMessage?.createdAt)}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-600">
                {getConversationSubtitle(conversation)}
              </p>
              {conversation?.conversationType === 'group' && (
                <div className="flex items-center text-xs text-gray-500">
                  <Users className="w-3 h-3 mr-1" />
                  Group
                </div>
              )}
            </div>

            {/* Latest Message Preview */}
            {conversation?.latestMessage && (
              <p className={`text-xs truncate mt-1 ${
                conversation?.hasUnreadMessages ? 'text-gray-900 font-medium' : 'text-gray-500'
              }`}>
                {conversation?.latestMessage?.sender?.fullName}: {conversation?.latestMessage?.content}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ContactList;